/**
 * Sous-formulaire — infos tenant (sans branding ni admin SaaS).
 *
 * Whitelist 7.D limitée aux champs disponibles via `GET /v1/auth/me` :
 *   name, locale, timezone, currency, logo_url.
 *
 * Les champs `legal_name`, `email`, `phone`, `address` (objet) viendront
 * quand un endpoint `/v1/tenants/me` les exposera, ou quand /auth/me sera
 * étendu — chunk dédié.
 */

import { useTranslation } from "react-i18next";

export type TenantInfoFormState = {
  name: string;
  locale: "fr" | "en";
  timezone: string;
  currency: string;
  logo_url: string;
};

type Props = {
  value: TenantInfoFormState;
  onChange: (next: TenantInfoFormState) => void;
  disabled?: boolean;
};

export function TenantInfoForm({ value, onChange, disabled }: Props) {
  const { t } = useTranslation();
  const set = <K extends keyof TenantInfoFormState>(key: K, v: TenantInfoFormState[K]) =>
    onChange({ ...value, [key]: v });

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <div className="md:col-span-2">
        <label className="label" htmlFor="tenant-name">
          {t("settings.fields.name")}
        </label>
        <input
          id="tenant-name"
          className="input"
          type="text"
          value={value.name}
          onChange={(e) => set("name", e.target.value)}
          disabled={disabled}
          required
          minLength={2}
          maxLength={200}
        />
      </div>

      <div>
        <label className="label" htmlFor="tenant-locale">
          {t("settings.fields.locale")}
        </label>
        <select
          id="tenant-locale"
          className="input"
          value={value.locale}
          onChange={(e) => set("locale", e.target.value as "fr" | "en")}
          disabled={disabled}
        >
          <option value="fr">Français</option>
          <option value="en">English</option>
        </select>
      </div>

      <div>
        <label className="label" htmlFor="tenant-currency">
          {t("settings.fields.currency")}
        </label>
        <input
          id="tenant-currency"
          className="input"
          type="text"
          value={value.currency}
          onChange={(e) => set("currency", e.target.value.toUpperCase())}
          disabled={disabled}
          maxLength={3}
          minLength={3}
          placeholder="CAD"
        />
      </div>

      <div className="md:col-span-2">
        <label className="label" htmlFor="tenant-timezone">
          {t("settings.fields.timezone")}
        </label>
        <input
          id="tenant-timezone"
          className="input"
          type="text"
          value={value.timezone}
          onChange={(e) => set("timezone", e.target.value)}
          disabled={disabled}
          placeholder="America/Montreal"
        />
      </div>

      <div className="md:col-span-2">
        <label className="label" htmlFor="tenant-logo-url">
          {t("settings.fields.logo_url")}
        </label>
        <input
          id="tenant-logo-url"
          className="input"
          type="url"
          value={value.logo_url}
          onChange={(e) => set("logo_url", e.target.value)}
          disabled={disabled}
          placeholder="https://exemple.com/logo.png"
          maxLength={500}
        />
        <p className="mt-1 text-xs text-slate-500">{t("settings.fields.logo_url_hint")}</p>
      </div>
    </div>
  );
}
